/**
 * The local copy of the account, and the only file that opens a transaction.
 *
 * Screens read from here, never from the network. That is what "offline-first"
 * costs and buys: a list is on screen because it is on the device, so being on
 * a train is indistinguishable from being at a desk until something needs
 * saying.
 *
 * The queue lives in the same database on purpose. A row and the mutation that
 * explains it are written in **one transaction** — split in two, a crash in
 * between either loses the change or leaves a row the server never hears about.
 */

import type { Creator, Item, List, ListKind } from "../lib/api.js";
import type { ShareMode } from "../lib/sharing.js";
import { deleteDatabase, deleteNamedDatabase } from "./db.js";
import { forgetSharedScope, registeredScopes } from "./scopes.js";

/** Caller closes the account's store first. Never open a new mirror during cleanup. */
export function eraseAccountMirror(userId: string): Promise<void> {
  return deleteDatabase(userId);
}

/**
 * The shared lists a signed-in visitor opened in this browser (g71). They
 * are that account's view of someone else's list, and go when the account
 * signs out or is deleted, as its own mirror does. A guest's copies are the
 * browser's, like the guest cookie, and stay. Best effort: a copy another tab
 * still has open is left for that tab.
 */
export async function eraseSignedInShares(): Promise<void> {
  for (const entry of registeredScopes().filter((row) => row.visitorKind === "account")) {
    try {
      await deleteNamedDatabase(entry.db);
      forgetSharedScope(entry.db);
    } catch {
      // Blocked by another tab. Its registry entry stays, so a later sign-out
      // or prune finds it again.
    }
  }
}
import { editOccurrence, occurrence } from "../lib/occurrence.js";
import { BY_KEY, BY_LIST, ITEMS, LISTS, META, PENDING, IMPORTS, ORDERS, collect, committed, done } from "./db.js";
import type { ImportRecord, ImportMutation } from "./imports.js";
import {
  coalesce,
  folded,
  isListOrderChange,
  mergeFields,
  pendingKey,
  whole,
  type Draft,
  type Fields,
  type Pending,
} from "./queue.js";
import type { AddedEntry, Delta, GroupAck } from "./transport.js";
import { recordsActions, recordsGroups, type ActivityStamp, type DeclaredGroupKind } from "../../api/src/shared/activity.js";
import {
  DETAIL_PREFIX, HISTORY_PREFIX, SERIES_PREFIX, STAMP_KEY, detailKey, evictions, readsStamp, seriesKey,
  type DetailRow, type HistoryTarget, type SeriesRow,
} from "./history.js";

/** What a screen is told changed. `row: undefined` means it is gone. */
export type Change =
  | { entity: "list"; id: string; row: List | undefined }
  | { entity: "item"; id: string; listId: string; row: Item | undefined }
  /** A creator's name arrived or changed (g68). Creators are never removed here. */
  | { entity: "creator"; id: string; row: Creator }
  /**
   * Where the owner's history stands moved (g110), or history stopped being
   * readable (`null`). Kept pages a moved `epoch` made stale are already gone.
   */
  | { entity: "activity"; stamp: ActivityStamp | null }
  /** The shared lists the account added (g132), whole, as the last reply said them. */
  | { entity: "added"; entries: AddedList[] };

export type RowWrite =
  | { entity: "list"; op: "put"; row: List }
  | { entity: "item"; op: "put"; row: Item }
  | { entity: "list"; op: "remove"; id: string }
  | { entity: "item"; op: "remove"; id: string }
  /**
   * A row's category and nothing else, as switching Order by category on
   * fills it (g81). Read and rewritten inside the transaction, so an edit to
   * the row's text that landed a moment earlier is kept rather than
   * overwritten by a whole-row copy taken before it.
   */
  | { entity: "item"; op: "category"; id: string; category: string | null };

/** One queued mutation's fate, decided by the server's verdict for it. */
export interface Resolution {
  seq: number;
  imported?: { id: string; rejected?: string };
  /** Absent for an accepted mutation; present when the local row has to go back. */
  revert?: RowWrite;
  /** What the server said became of a whole action (g107). */
  settled?: Settled;
  /**
   * With `revert`, when the change also wrote rows no record names: the cursor
   * to fetch again from, so the server's copy of them replaces this device's (g81).
   */
  rewind?: number;
}

export interface Snapshot {
  lists: List[];
  items: Item[];
  creators: Creator[];
  cursor: number;
  everSynced: boolean;
  /** The last owner reply's history stamp (g110). Null while history cannot be read. */
  activity: ActivityStamp | null;
  /** The shared lists the account added (g132), and the ones removed here and not yet sent. */
  added: AddedList[];
  removing: string[];
}

/**
 * The fields a mutation can carry, and therefore the only ones a pending change
 * may put back on top of a server row. `createdAt`, `updatedAt` and `deletedAt`
 * are the server's to say — rebasing those would hand a device its own clock as
 * the truth.
 */
const LIST_FIELDS = ["name", "kind", "orderBy"] as const;
// No `orderKey`: it is the server's to assign, so a pending change never
// carries one and must never rebase one back over a delta.
const ITEM_FIELDS = ["listId", "text", "done", "quantity", "category", "note", "due", "repeat"] as const;

/**
 * What counts as a *visible* change, which is a different question from what a
 * pending mutation may rebase — and was the same list until it turned out that
 * mattered.
 *
 * `orderKey` is here and deliberately not above. A row that moved has changed
 * where it sits, so a screen has to hear about it; but a queued mutation still
 * may not put a key back over the server's, which is what adding it to
 * `ITEM_FIELDS` would have allowed.
 */
//
// `creatorId` (g68) is the server's alone, like `orderKey`, and visible for the
// same reason: a local create has none until its echo arrives, and a screen
// that shows who made a row must hear when it gets one.
//
// `sharing` (g71) is the owner's server's alone too, and visible because an
// open list starts or stops refreshing itself when it changes.
const LIST_VISIBLE = [...LIST_FIELDS, "orderKey", "creatorId", "sharing"] as const;
const ITEM_VISIBLE = [...ITEM_FIELDS, "orderKey", "creatorId", "lastDoneOn", "repeatAnchor", "occurrenceRevision", "completion"] as const;

/**
 * Who made what (g68) lives in `meta`, one row per creator under this prefix,
 * so the database keeps its version and an older build can still open it.
 */
const CREATOR_KEY = "creator:";

/**
 * `creatorsSynced` (g68) says the last reply carried creators. Until one has,
 * the rows here may predate `creatorId`, and an incremental pull would never
 * bring it, since those rows have not changed on the server. So the engine
 * pulls from 0. A reply without creators -- from a build older than g68, say
 * during a rollback, whose rows lack `creatorId` too -- clears it again.
 */
export interface Meta {
  cursor: number;
  everSynced: boolean;
  creatorsSynced: boolean;
  /** Every list here carries its `sharing` (g71); until then an account pulls from 0. */
  sharingSynced: boolean;
  /** A shared scope whose link stopped working, and why (g71). Nothing syncs again. */
  ended?: ScopeEnd;
  /**
   * The last reply said its server records history (g107). While it holds, a
   * change is queued as a whole action; `commitLocal` reads it inside the
   * write's own transaction, so two tabs of one scope cannot disagree.
   */
  recording: boolean;
  /** The last reply said its server also takes groups (g108). Never true without `recording`. */
  recordingGroups: boolean;
  /** Shared lists removed from the account's Lists on this device and not yet sent (g132). */
  removing: string[];
}

/** `meta`'s key for `Meta.recording`. */
const RECORDING = "recording";

/** `meta`'s key for `Meta.recordingGroups`. */
const RECORDING_GROUPS = "recordingGroups";

/**
 * What became of each child of a group (g107), kept under `group:<id>` in
 * `meta` so the database keeps its version.
 *
 * `openGroup` writes the whole ledger before any child is queued (g108):
 * every expected child, each `pending`. A child stays `pending` until the
 * answer that settles it, so a restart in between finds the group as it
 * stood. One that will never be queued is `unsent`. The engine reads the
 * ledger to tell the server the group's manifest and the children it will
 * never send, and `settle` removes it once the server has said the group is
 * closed, or once this device has nothing left to say of it.
 */
export type ChildState = "pending" | "unsent" | "applied" | "unchanged" | "refused";

export interface GroupLedger {
  id: string;
  /** How many children the group expects. */
  size: number;
  kind: DeclaredGroupKind;
  openedAt: number;
  /** A reply has acknowledged the manifest, so it is not sent again. */
  declared: boolean;
  /** `told`: the server has been told this child will not be sent. */
  children: Record<string, { index: number; state: ChildState; reason?: string; told?: boolean }>;
}

const GROUP_KEY = "group:";

/** A child the server has no outcome for unless this device says so: never queued, or refused before the server judged it. */
export const untold = (ledger: GroupLedger): number[] => Object.values(ledger.children)
  .filter((child) => (child.state === "unsent" || child.state === "refused") && !child.told).map((child) => child.index).sort((a, b) => a - b);

const waiting = (ledger: GroupLedger): boolean => Object.values(ledger.children).some((child) => child.state === "pending");

/** What the server said became of a whole action (g107). Absent for a folded record. */
export interface Settled {
  operationId: string;
  state: Exclude<ChildState, "pending">;
  /** The server recorded it and said so; `false` where it accepted without a receipt. */
  recorded: boolean;
  /** An answer to a command the server had already accepted. */
  replay: boolean;
  reason?: string;
  group?: string;
}

/** Every group this scope holds a ledger for. */
export async function readGroups(db: IDBDatabase): Promise<GroupLedger[]> {
  const rows = await collect<{ key: string; value: unknown }>(db.transaction(META, "readonly").objectStore(META));
  return rows.filter((row) => row.key.startsWith(GROUP_KEY)).map((row) => row.value as GroupLedger);
}

interface ChildNote { operationId: string; state: ChildState; reason?: string }

/**
 * Every note for one group, and what the server said of it, in one read and
 * one write: two reads of a ledger queued before either write would each put
 * back what the other changed. Nothing here makes a ledger: only `openGroup`
 * does, and a note for a group this device holds no ledger for is dropped.
 *
 * The ledger goes once the server says the group is closed or refuses it, and
 * once an acknowledged push leaves this device nothing more to say: no child
 * still queued and none untold. A group the server does not know is declared
 * again from the start.
 */
function amendLedger(meta: IDBObjectStore, groupId: string, notes: ChildNote[], ack?: GroupAck): void {
  const key = GROUP_KEY + groupId;
  const read = meta.get(key) as IDBRequest<{ key: string; value: GroupLedger } | undefined>;
  read.onsuccess = () => {
    const ledger = read.result?.value;
    if (!ledger) return;
    for (const note of notes) {
      const child = ledger.children[note.operationId];
      // A verdict for a child this ledger never expected is not made one of its children.
      if (!child) continue;
      ledger.children[note.operationId] = { index: child.index, state: note.state, ...(note.reason !== undefined ? { reason: note.reason } : {}) };
    }
    if (ack?.status === "closed" || ack?.status === "refused") { meta.delete(key); return; }
    if (ack?.status === "unknown") {
      ledger.declared = false;
      for (const child of Object.values(ledger.children)) delete child.told;
    }
    if (ack?.status === "open") {
      ledger.declared = true;
      const said = new Set(ack.notSent);
      for (const child of Object.values(ledger.children)) if (said.has(child.index) && child.state !== "pending") child.told = true;
      if (!waiting(ledger) && untold(ledger).length === 0) { meta.delete(key); return; }
    }
    meta.put({ key, value: ledger });
  };
}

/**
 * Opens a group before its first child is queued (g108): the whole manifest,
 * every child `pending`, in one transaction. Only against a server whose last
 * reply said it records and takes groups; otherwise nothing is written, and
 * the children are queued as ordinary changes. Answers whether it opened.
 */
export function openGroup(db: IDBDatabase, group: { id: string; kind: DeclaredGroupKind; operations: readonly string[] }, now = Date.now()): Promise<boolean> {
  const tx = db.transaction(META, "readwrite");
  const meta = tx.objectStore(META);
  // Written true only by a reply that also said it records (`recordsGroups`).
  const groups = meta.get(RECORDING_GROUPS) as IDBRequest<{ value: unknown } | undefined>;
  let opened = false;
  groups.onsuccess = () => {
    if (groups.result?.value !== true) return;
    const ledger: GroupLedger = { id: group.id, size: group.operations.length, kind: group.kind, openedAt: now, declared: false,
      children: Object.fromEntries(group.operations.map((operationId, index) => [operationId, { index, state: "pending" as const }])) };
    meta.put({ key: GROUP_KEY + group.id, value: ledger });
    opened = true;
  };
  return committed(tx).then(() => opened);
}

/** Children of a group that will never be queued (g108): the row changed before its removal, or the write did not take. */
export function markUnsent(db: IDBDatabase, groupId: string, operationIds: readonly string[]): Promise<void> {
  const tx = db.transaction(META, "readwrite");
  amendLedger(tx.objectStore(META), groupId, operationIds.map((operationId) => ({ operationId, state: "unsent" as const })));
  return committed(tx);
}

/** How long a group may be writing its children before a child with no queued record is taken as never queued. */
export const GROUP_WRITING_MS = 60_000;

/**
 * A group interrupted before all its children were queued (g108): the page
 * closed mid-clear. A child still `pending` with no record in the queue was
 * never queued and never will be, so it is `unsent`. Run by the engine, only
 * where a ledger is held, until a run has looked at every one.
 * A group opened within the last minute is left alone, since another tab of
 * this scope may be writing its children now. Answers whether it left one
 * alone for that reason, so the engine looks again.
 */
export function reconcileGroups(db: IDBDatabase, now = Date.now()): Promise<boolean> {
  let young = false;
  const tx = db.transaction([PENDING, META], "readwrite");
  const meta = tx.objectStore(META);
  const queued = tx.objectStore(PENDING).getAll() as IDBRequest<Pending[]>;
  queued.onsuccess = () => {
    const held = new Set(queued.result.flatMap((record) => record.group && record.operationId ? [record.operationId] : []));
    const rows = meta.getAll() as IDBRequest<{ key: string; value: unknown }[]>;
    rows.onsuccess = () => {
      for (const row of rows.result) {
        if (!row.key.startsWith(GROUP_KEY)) continue;
        const ledger = row.value as GroupLedger;
        if (now - ledger.openedAt < GROUP_WRITING_MS) { young = true; continue; }
        let changed = false;
        for (const [operationId, child] of Object.entries(ledger.children)) {
          if (child.state !== "pending" || held.has(operationId)) continue;
          ledger.children[operationId] = { index: child.index, state: "unsent" };
          changed = true;
        }
        if (changed) meta.put({ key: row.key, value: ledger });
      }
    };
  };
  return committed(tx).then(() => young);
}

/**
 * A shared list the account added to its Lists (g132), as this device keeps
 * it. An ended one keeps the name, kind and owner this device last saw, if it
 * ever saw them live; the server says nothing of a list whose link ended.
 */
export interface AddedList {
  grantId: string;
  mode: ShareMode;
  addedAt: number;
  state: "live" | "ended";
  name: string | null;
  kind: ListKind | null;
  sharedBy: string | null;
}

/**
 * The account's added lists, kept whole under one `meta` key, so the database
 * keeps its version and an older build can still open it. Removals made here
 * wait under another until a reply that carries the set says they are gone.
 */
const ADDED_KEY = "addedLists";
const REMOVING_KEY = "addedRemoving";

const removals = (value: unknown): string[] => Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : [];

/**
 * A reply's set, kept: each entry as the server said it, an ended one with
 * what this device last knew of it. Replaces the whole set, so an entry
 * removed on another device goes.
 */
function absorbAdded(meta: IDBObjectStore, held: unknown, incoming: AddedEntry[], changes: Change[]): void {
  const known = new Map((Array.isArray(held) ? held as AddedList[] : []).map((entry) => [entry.grantId, entry]));
  const entries = incoming.map((entry): AddedList => {
    const base = { grantId: entry.grantId, mode: entry.mode, addedAt: entry.addedAt };
    if (entry.state === "live") return { ...base, state: "live", name: entry.name, kind: entry.kind, sharedBy: entry.sharedBy };
    const last = known.get(entry.grantId);
    return { ...base, state: "ended", name: last?.name ?? null, kind: last?.kind ?? null, sharedBy: last?.sharedBy ?? null };
  });
  meta.put({ key: ADDED_KEY, value: entries });
  changes.push({ entity: "added", entries });
}

/**
 * Takes a shared list out of the account's Lists on this device (g132), to be
 * sent with the next push. Answers the removals now waiting.
 */
export function queueAddedRemoval(db: IDBDatabase, grantId: string): Promise<string[]> {
  const tx = db.transaction(META, "readwrite");
  const meta = tx.objectStore(META);
  const held = meta.get(REMOVING_KEY) as IDBRequest<{ key: string; value: unknown } | undefined>;
  let waiting: string[] = [];
  held.onsuccess = () => {
    const current = removals(held.result?.value);
    waiting = current.includes(grantId) ? current : [...current, grantId];
    meta.put({ key: REMOVING_KEY, value: waiting });
  };
  return committed(tx).then(() => waiting);
}

/** Why a shared scope ended (g71). */
export type EndReason = "unavailable" | "forbidden" | "identity_changed" | "scope_changed";

export interface ScopeEnd {
  reason: EndReason;
  at: number;
}

const SCOPE_ENDED = "scopeEnded";

export async function readMeta(db: IDBDatabase): Promise<Meta> {
  const tx = db.transaction(META, "readonly");
  const store = tx.objectStore(META);
  const rows = await collect<{ key: string; value: unknown }>(store);
  const value = (key: string) => rows.find((row) => row.key === key)?.value;
  const ended = value(SCOPE_ENDED) as ScopeEnd | undefined;
  return {
    cursor: typeof value("cursor") === "number" ? (value("cursor") as number) : 0,
    everSynced: value("everSynced") === true,
    creatorsSynced: value("creatorsSynced") === true,
    sharingSynced: value("sharingSynced") === true,
    recording: value(RECORDING) === true,
    recordingGroups: value(RECORDING_GROUPS) === true,
    removing: removals(value(REMOVING_KEY)),
    ...(ended && typeof ended.reason === "string" ? { ended } : {}),
  };
}

/**
 * A shared scope's link stopped working (g71): every queued change is marked
 * unsent, with the reason, and the scope is marked ended. One transaction, so
 * a reload finds both or neither. The records stay, for the person to see
 * what did not go; nothing sends them again, and a queue stamped here can
 * never be sent by another link or visitor, since each has its own database.
 */
export function endScope(db: IDBDatabase, reason: EndReason, at = Date.now()): Promise<void> {
  const tx = db.transaction([PENDING, META], "readwrite");
  const pending = tx.objectStore(PENDING);
  const all = pending.openCursor();
  all.onsuccess = () => {
    const cursor = all.result;
    if (!cursor) return;
    const record = cursor.value as Pending;
    cursor.update({ ...record, inflight: false, unsent: record.unsent ?? reason });
    cursor.continue();
  };
  const meta = tx.objectStore(META);
  const found = meta.get(SCOPE_ENDED) as IDBRequest<{ key: string; value: ScopeEnd } | undefined>;
  found.onsuccess = () => {
    if (!found.result) meta.put({ key: SCOPE_ENDED, value: { reason, at } });
  };
  return committed(tx);
}

export async function readSnapshot(db: IDBDatabase): Promise<Snapshot> {
  const tx = db.transaction([LISTS, ITEMS, META], "readonly");
  const [lists, items, meta] = await Promise.all([
    collect<List>(tx.objectStore(LISTS)),
    collect<Item>(tx.objectStore(ITEMS)),
    collect<{ key: string; value: unknown }>(tx.objectStore(META)),
  ]);
  const value = (key: string) => meta.find((row) => row.key === key)?.value;
  return {
    lists,
    items,
    creators: meta.filter((row) => row.key.startsWith(CREATOR_KEY)).map((row) => row.value as Creator),
    cursor: typeof value("cursor") === "number" ? (value("cursor") as number) : 0,
    everSynced: value("everSynced") === true,
    activity: readsStamp(value(STAMP_KEY)) ? (value(STAMP_KEY) as ActivityStamp) : null,
    added: Array.isArray(value(ADDED_KEY)) ? (value(ADDED_KEY) as AddedList[]) : [],
    removing: removals(value(REMOVING_KEY)),
  };
}

/** In `seq` order, which is the order they were made in. */
export function readPending(db: IDBDatabase): Promise<Pending[]> {
  return collect<Pending>(db.transaction(PENDING, "readonly").objectStore(PENDING));
}

/**
 * A local change: the rows it touches and the mutation that explains them, in
 * one transaction.
 *
 * **Which kind of record it becomes is decided here** (g107), from what the
 * last reply said, read in this transaction: a whole action while the server
 * records, a folded record while it does not.
 *
 * While folding, deleting a list sweeps the queue as well as the mirror. A
 * pending `item.upsert` for a list that is going away would come back from
 * the server as `list_not_found`, and a banner seconds after a deliberate
 * delete is a bug report waiting to be filed. A whole action is never swept:
 * it happened, so it is sent, ahead of the delete that follows it.
 *
 * A change keeps its `group` only where that group's ledger is here, read in
 * this transaction (g108): `openGroup` writes it first, and a child of a
 * group that was never opened is an ordinary change.
 *
 * It answers with the operation id the change was queued under, where it
 * became a whole action, and nothing where it was folded.
 */
export interface Queued { operationId?: string }

export function commitLocal(db: IDBDatabase, writes: RowWrite[], draft: Draft): Promise<Queued> {
  const queuedAs: Queued = {};
  const tx = db.transaction([LISTS, ITEMS, PENDING, IMPORTS, META], "readwrite");
  const stores = { list: tx.objectStore(LISTS), item: tx.objectStore(ITEMS) };
  const pending = tx.objectStore(PENDING);
  const imports = tx.objectStore(IMPORTS);
  const meta = tx.objectStore(META);
  // Answered before the read below, which is where it is used.
  const mode = meta.get(RECORDING) as IDBRequest<{ value: unknown } | undefined>;
  const ledger = draft.group ? meta.get(GROUP_KEY + draft.group.id) as IDBRequest<unknown> : undefined;
  const reading = imports.getAll() as IDBRequest<ImportRecord[]>;
  reading.onsuccess = () => {
    const acting = mode.result?.value === true && draft.captured !== undefined;
    draft = acting ? whole(draft) : folded(draft);
    if (draft.group && ledger?.result === undefined) { const { group: _group, ...ungrouped } = draft; draft = ungrouped; }
    const dependencies = new Set(draft.blockedBy ?? []);
    for (const entry of reading.result) {
      const origin = entry.originIds.includes(draft.id);
      const destination = draft.fields.listId ?? draft.listId;
      const belongs = entry.list.id === draft.id || origin || entry.list.id === destination;
      if (entry.status === "rejected" && belongs) {
        entry.revision = (entry.revision ?? 0) + 1;
        // An edit can commit just after the refusal transaction. Keep that edit in
        // recovery rather than resurrecting a row whose creation failed.
        for (const write of writes) {
          if (write.op === "category") continue;
          if (write.entity === "list" && write.op === "put") entry.list = write.row;
          if (write.entity === "item") {
            const id = write.op === "put" ? write.row.id : write.id;
            entry.rows = (entry.rows ?? []).filter(row => row.id !== id);
            if (write.op === "put") { entry.rows.push(write.row); if (!entry.originIds.includes(id)) entry.originIds.push(id); }
            stores.item.delete(id);
          } else stores.list.delete(entry.list.id);
        }
        imports.put(entry);
        return;
      }
      if (entry.status !== "pending") continue;
      if (belongs || [draft.fields.afterId, draft.fields.beforeId].some(id =>
        id === entry.list.id || entry.originIds.includes(String(id)))) dependencies.add(entry.id);
      if (draft.entity === "item" && draft.before === null && entry.list.id === destination && !origin) {
        entry.originIds.push(draft.id); imports.put(entry);
      }
    }
    if (dependencies.size) draft = { ...draft, blockedBy: [...dependencies] };
    // Said only once the change is going to be queued: one kept as a refused import's draft above queues nothing.
    if (acting && draft.operationId) queuedAs.operationId = draft.operationId;

  const edits: RowEdit[] = [];
  for (const write of writes) {
    if (write.op === "put") stores[write.entity].put(write.row);
    else if (write.op === "category") edits.push([write.id, row => ({ ...row, category: write.category })]);
    else stores[write.entity].delete(write.id);
  }
  rewriteRows(stores.item, edits);

  // Only what was folded may be swept, and only by a folded delete (g107).
  const sweepable = (record: Pending) => !record.inflight && record.actionVersion === undefined;
  if (!acting && draft.entity === "list" && draft.op === "delete") {
    const byList = pending.index(BY_LIST).openCursor(IDBKeyRange.only(draft.id));
    byList.onsuccess = () => {
      const cursor = byList.result;
      if (!cursor) return;
      if (sweepable(cursor.value as Pending)) cursor.delete();
      cursor.continue();
    };
  }

  /**
   * A deleted row's queued move lives under its own key, so coalescing never sees
   * it, and sending it would ask the server to move a row that is gone and get
   * a rejection nobody can act on. A whole action's move stays and is sent
   * first (g107): it was made, and the server still holds the row to move.
   */
  if (!acting && draft.op === "delete") {
    const moveKey = pendingKey(draft.entity, draft.id, "move");
    const queued = pending.index(BY_KEY).openCursor(IDBKeyRange.only(moveKey));
    queued.onsuccess = () => {
      const cursor = queued.result;
      if (!cursor) return;
      if (sweepable(cursor.value as Pending)) cursor.delete();
      cursor.continue();
    };
  }

  // Newest first: for one index key, "prev" walks the primary keys — the `seq`
  // values — downwards, so the first hit is the latest record for this entity.
  const key = pendingKey(draft.entity, draft.id, draft.op);
  const newest = pending.index(BY_KEY).openCursor(IDBKeyRange.only(key), "prev");
  newest.onsuccess = () => {
    const existing = newest.result?.value as Pending | undefined;
    const outcome = coalesce(existing, draft);
    if (outcome.action === "insert") pending.add(outcome.record);
    else if (outcome.action === "update") pending.put(outcome.record);
    else if (outcome.action === "drop") pending.delete(outcome.seq);
  };

  };

  return committed(tx).then(() => queuedAs);
}

/** One field rewrite of a stored row, or `undefined` to leave that row as it is. */
type RowEdit = [id: string, edit: (row: Item) => Item | undefined];

/**
 * Rewrites only the fields the edits name, such as a category (g81), on rows
 * read inside the transaction; a row already gone stays gone.
 * Every edit to one row runs on one read of it and is stored by one write:
 * two reads of a row queued before either write would each put back what the
 * other changed.
 */
function rewriteRows(store: IDBObjectStore, edits: RowEdit[], onPut?: (row: Item) => void): void {
  const byRow = new Map<string, RowEdit[1][]>();
  for (const [id, edit] of edits) byRow.set(id, [...byRow.get(id) ?? [], edit]);
  for (const [id, steps] of byRow) {
    const found = store.get(id) as IDBRequest<Item | undefined>;
    found.onsuccess = () => {
      if (!found.result) return;
      let row = found.result;
      let changed = false;
      for (const step of steps) {
        const next = step(row);
        if (next) { row = next; changed = true; }
      }
      if (!changed) return;
      store.put(row);
      onPut?.(row);
    };
  }
}

/**
 * Moves the sync cursor back to `to`, never forward, so rows this device holds
 * a copy of that was never the server's are fetched again. Applying a delta
 * twice is harmless, missing one is not. No number, no rewind to guess at:
 * back to the start.
 */
function rewind(meta: IDBObjectStore, to: unknown): void {
  const target = typeof to === "number" ? to : 0;
  const read = meta.get("cursor") as IDBRequest<{ value: unknown } | undefined>;
  read.onsuccess = () => {
    const current = read.result?.value;
    if (typeof current !== "number" || current > target) meta.put({ key: "cursor", value: target });
  };
}

/**
 * Retires what the one-time Auto-order left behind (g62, removed by g81).
 *
 * The release before g81 could leave an Auto-order or its Undo queued, with
 * the order it had arranged on screen. This build has no such command, and
 * the server refuses it. Sent as it stands, the record would read as a list
 * create and its refusal would take the list off the device. So it goes
 * before the queue is read, with the store Undo read from, and the cursor
 * goes back to the start: the next sync replaces the order and the
 * categories that command put here with the server's.
 */
export function retireLegacyOrders(db: IDBDatabase): Promise<void> {
  const tx = db.transaction([PENDING, ORDERS, META], "readwrite");
  const pending = tx.objectStore(PENDING);
  let retired = false;
  const walk = pending.openCursor();
  walk.onsuccess = () => {
    const cursor = walk.result;
    if (!cursor) {
      if (retired) rewind(tx.objectStore(META), 0);
      return;
    }
    const op: string = (cursor.value as { op: string }).op;
    if (op === "autoOrder" || op === "undoAutoOrder") {
      cursor.delete();
      retired = true;
    }
    cursor.continue();
  };
  tx.objectStore(ORDERS).clear();
  return committed(tx);
}

export function readImports(db: IDBDatabase): Promise<ImportRecord[]> {
  return collect<ImportRecord>(db.transaction(IMPORTS, "readonly").objectStore(IMPORTS));
}

export function discardImport(db: IDBDatabase, id: string): Promise<void> {
  const tx = db.transaction(IMPORTS, "readwrite");
  const store = tx.objectStore(IMPORTS);
  const request = store.get(id) as IDBRequest<ImportRecord | undefined>;
  request.onsuccess = () => { if (request.result?.status === "rejected") store.delete(id); };
  return committed(tx);
}

/** The whole local creation and its replay identity commit together. */
export function commitImport(db: IDBDatabase, list: List, rows: Item[], mutation: ImportMutation, retryId?: string,
  action?: Pick<Draft, "captured" | "actorScope">): Promise<void> {
  const tx = db.transaction([LISTS, ITEMS, PENDING, IMPORTS, META], "readwrite");
  tx.objectStore(LISTS).add(list);
  for (const row of rows) tx.objectStore(ITEMS).add(row);
  const record: ImportRecord = { id: mutation.operationId, list, originIds: rows.map(row => row.id), status: "pending" };
  tx.objectStore(IMPORTS).add(record);
  const draft: Draft = { entity: "list", id: list.id, op: "import", listId: null,
    operationId: mutation.operationId, ordered: true, before: null, fields: { ...mutation }, ...action };
  // A whole action while the server records (g107), decided as `commitLocal` decides it.
  const mode = tx.objectStore(META).get(RECORDING) as IDBRequest<{ value: unknown } | undefined>;
  mode.onsuccess = () => {
    const pending = coalesce(undefined, mode.result?.value === true && draft.captured ? whole(draft) : folded(draft));
    if (pending.action !== "insert") { tx.abort(); return; }
    tx.objectStore(PENDING).add(pending.record);
  };
  if (retryId) {
    const old = tx.objectStore(IMPORTS).get(retryId) as IDBRequest<ImportRecord | undefined>;
    old.onsuccess = () => {
      if (old.result?.status !== "rejected") { tx.abort(); return; }
      tx.objectStore(IMPORTS).delete(retryId);
    };
  }
  return committed(tx);
}

/**
 * Marked before the request goes out, never after — `sent` has to be true by
 * the time the bytes could possibly have reached the server.
 */
export function markInflight(db: IDBDatabase, seqs: number[]): Promise<void> {
  return stamp(db, seqs, (record) => ({ ...record, inflight: true, sent: true }));
}

/**
 * The attempt ended without a verdict for these records — a transport failure,
 * a broken server, a refused body. Nothing is coming that names their `seq`, so
 * they go back to being foldable rather than staying frozen for the rest of the
 * session.
 *
 * Called with no `seqs` it releases everything, which is how a reload recovers
 * from a tab that died mid-request.
 */
export function releaseInflight(db: IDBDatabase, seqs?: number[]): Promise<void> {
  return stamp(db, seqs, (record) => ({ ...record, inflight: false }));
}

function stamp(
  db: IDBDatabase,
  seqs: number[] | undefined,
  change: (record: Pending) => Pending,
): Promise<void> {
  const tx = db.transaction(PENDING, "readwrite");
  const pending = tx.objectStore(PENDING);

  if (seqs === undefined) {
    const all = pending.openCursor();
    all.onsuccess = () => {
      const cursor = all.result;
      if (!cursor) return;
      cursor.update(change(cursor.value as Pending));
      cursor.continue();
    };
    return committed(tx);
  }

  for (const seq of seqs) {
    const request = pending.get(seq) as IDBRequest<Pending | undefined>;
    request.onsuccess = () => {
      const record = request.result;
      if (record) pending.put(change(record));
    };
  }
  return committed(tx);
}

export function applyDelta(db: IDBDatabase, delta: Delta): Promise<Change[]> {
  return settle(db, [], delta);
}

/**
 * The server's answer, applied whole: what the verdicts decided, then the delta,
 * then the cursor — in one transaction, so the cursor can never move over a
 * partially stored delta.
 *
 * `delta` is absent when there are only verdicts to act on, which happens when
 * the server refused the request outright. The cursor stays where it is and
 * `everSynced` stays as it was: nothing was downloaded, so claiming otherwise
 * would tell a fresh device it has seen the account when it has not.
 *
 * The returned changes carry **both** halves: what a revert undid, and what the
 * delta said. A caller that reports them is telling the screen everything that
 * moved, which is the whole point of reverting in the first place.
 */
export function settle(
  db: IDBDatabase,
  resolutions: Resolution[],
  delta?: Delta,
): Promise<Change[]> {
  const tx = db.transaction([LISTS, ITEMS, META, PENDING, IMPORTS], "readwrite");
  const stores = { list: tx.objectStore(LISTS), item: tx.objectStore(ITEMS) };
  const meta = tx.objectStore(META);
  const pending = tx.objectStore(PENDING);
  const changes: Change[] = [];

  // A grouped child's fate is kept in the transaction that takes it off the queue (g107),
  // with what the reply said of its group (g108).
  const fates = new Map<string, ChildNote[]>();
  for (const resolution of resolutions) {
    pending.delete(resolution.seq);
    const settled = resolution.settled;
    if (!settled?.group) continue;
    fates.set(settled.group, [...fates.get(settled.group) ?? [],
      { operationId: settled.operationId, state: settled.state, ...(settled.reason !== undefined ? { reason: settled.reason } : {}) }]);
  }
  const acks = new Map((delta?.acks ?? []).map((ack) => [ack.id, ack]));
  for (const groupId of new Set([...fates.keys(), ...acks.keys()])) amendLedger(meta, groupId, fates.get(groupId) ?? [], acks.get(groupId));
  // After the delta's cursor is stored, where there is one: a rewind before it would be overwritten.
  const rewinds = () => {
    for (const resolution of resolutions) if (resolution.rewind !== undefined) rewind(meta, resolution.rewind);
  };

  /**
   * One revert per row: the earliest. Since g107 a row may have several
   * records refused in one answer, each holding the row as it stood before
   * its own change. Only the first of those is the row before any refused
   * change; a later one already contains an earlier refused change, and
   * putting it back would keep what the server refused.
   */
  const reverted = new Set<string>();
  const reverts = resolutions.flatMap((resolution) => {
    const revert = resolution.revert;
    if (!revert) return [];
    const row = `${revert.entity}:${"row" in revert ? revert.row.id : revert.id}`;
    if (reverted.has(row)) return [];
    reverted.add(row);
    return [revert];
  });

  // Nothing to put back and nothing downloaded: the queue shrank and no row on
  // any screen moved.
  if (reverts.length === 0 && !delta && !resolutions.some(row => row.imported)) { rewinds(); return committed(tx).then(() => changes); }

  /**
   * One read of what is still queued, for the reverts *and* the delta.
   *
   * The resolved records are deleted above, so what comes back is exactly the
   * work this device still believes in — and a revert has to be rebased on it
   * for the same reason a delta row does. Edit something, let it go on the
   * wire, type again while it is in flight, and the second edit cannot coalesce
   * into the first: it is a separate record, still queued, and still the newest
   * thing here. Putting the pre-first-edit row back over it and announcing that
   * is a screen rolling somebody's typing back under them.
   *
   * The mirror was already being clobbered this way before reverts were
   * reported at all; it was simply invisible until the screen was told.
   */
  // An owner's reply says where history stands (g110); a link's reply, which says who is looking, never does,
  // and its database never holds a stamp, so nothing is asked. Asked before the queue, so the answer is in
  // by the time the delta is stored: a transaction answers its requests in the order they were made.
  const heldStamp = delta && delta.scope === undefined ? meta.get(STAMP_KEY) as IDBRequest<{ key: string; value: unknown } | undefined> : undefined;
  // The account's added lists (g132), on an account's reply that carries them: what this device knew of each, and
  // the removals it has waiting, so the ones this push sent can go.
  const carriesAdded = delta !== undefined && delta.scope === undefined && delta.added !== undefined;
  const heldAdded = carriesAdded ? meta.get(ADDED_KEY) as IDBRequest<{ key: string; value: unknown } | undefined> : undefined;
  const heldRemoving = carriesAdded && delta.removedSent?.length ? meta.get(REMOVING_KEY) as IDBRequest<{ key: string; value: unknown } | undefined> : undefined;
  const all = pending.getAll() as IDBRequest<Pending[]>;
  const finish = (records: Pending[]) => {
    const rebase = rebaseIndex(records);

    for (const revert of reverts) {
      if (revert.op === "put") {
        // The same path a delta row takes, and for the same three reasons:
        // later queued fields win, an entity queued for deletion goes, and a
        // row that did not visibly change is not announced.
        if (revert.entity === "list") {
          absorb(stores.list, rebase, "list", LIST_FIELDS, LIST_VISIBLE, revert.row, (id, next) => {
            changes.push({ entity: "list", id, row: next });
          });
        } else {
          const incoming = revert.row;
          absorb(stores.item, rebase, "item", ITEM_FIELDS, ITEM_VISIBLE, incoming, (id, next) => {
            changes.push({ entity: "item", id, listId: next?.listId ?? incoming.listId, row: next });
          });
        }
        continue;
      }

      /**
       * A refused *create*, so the row goes. Read before deleting, for two
       * reasons `absorb` also has: an item's change carries the list it was in
       * and a `remove` revert names only an id, and a revert of a row that is
       * already gone is not something to announce.
       *
       * Not rebased, deliberately. Later edits to a row whose creation the
       * server refused are edits to something that never existed there; the row
       * goes and they drain against a server that will refuse them too.
       */
      const id = revert.id;
      if (revert.entity === "list") {
        const found = stores.list.get(id) as IDBRequest<List | undefined>;
        found.onsuccess = () => {
          if (!found.result) return;
          stores.list.delete(id);
          changes.push({ entity: "list", id, row: undefined });
        };
      } else {
        const found = stores.item.get(id) as IDBRequest<Item | undefined>;
        found.onsuccess = () => {
          const stored = found.result;
          if (!stored) return;
          stores.item.delete(id);
          changes.push({ entity: "item", id, listId: stored.listId, row: undefined });
        };
      }
    }

    if (!delta) { rewinds(); return; }

    // The delta is read on the server *after* the batch applies, so it echoes
    // this device's own writes — and anything typed since the push went out
    // would be overwritten by its own older echo. Rebasing the still-pending
    // fields back on top is what "per field, by arrival order" means when the
    // latest arrival is local.
    for (const row of delta.lists) {
      absorb(stores.list, rebase, "list", LIST_FIELDS, LIST_VISIBLE, row, (id, next) => {
        changes.push({ entity: "list", id, row: next });
      });
    }
    for (const row of delta.items) {
      absorb(stores.item, rebase, "item", ITEM_FIELDS, ITEM_VISIBLE, row, (id, next) => {
        changes.push({ entity: "item", id, listId: next?.listId ?? row.listId, row: next });
      });
    }

    // Gone to another of the owner's lists, where this link cannot follow
    // (g70). To this copy it is as good as deleted.
    for (const id of delta.departed ?? []) {
      const found = stores.item.get(id) as IDBRequest<Item | undefined>;
      found.onsuccess = () => {
        const stored = found.result;
        if (!stored) return;
        stores.item.delete(id);
        changes.push({ entity: "item", id, listId: stored.listId, row: undefined });
      };
    }

    if (delta.creators) absorbCreators(meta, delta.creators, changes);
    meta.put({ key: "creatorsSynced", value: delta.creators !== undefined });
    // Whether every list here carries its `sharing` (g71). Only a full download
    // can say yes, since an incremental one leaves unchanged lists as they
    // were; any reply with a list lacking it, from an older server, says no.
    const carriesSharing = delta.lists.every((row) => row.sharing !== undefined);
    if (!carriesSharing) meta.put({ key: "sharingSynced", value: false });
    else if (delta.from === 0) meta.put({ key: "sharingSynced", value: true });

    meta.put({ key: "cursor", value: delta.cursor });
    meta.put({ key: "everSynced", value: true });
    // What this reply's server does with `action` (g107). One that says nothing does not record.
    meta.put({ key: RECORDING, value: recordsActions(delta.recording) });
    meta.put({ key: RECORDING_GROUPS, value: recordsGroups(delta.recording) });
    if (heldStamp) absorbStamp(meta, heldStamp.result?.value, delta.activity, changes);
    if (heldAdded && delta.added) absorbAdded(meta, heldAdded.result?.value, delta.added, changes);
    if (heldRemoving) {
      const sent = new Set(delta.removedSent);
      meta.put({ key: REMOVING_KEY, value: removals(heldRemoving.result?.value).filter((id) => !sent.has(id)) });
    }
    rewinds();
  };
  all.onsuccess = () => {
    const next = (records: Pending[]) => {
      if (resolutions.some(row => row.imported)) resolveImports(tx, resolutions, records, changes, finish);
      else finish(records);
    };
    next(all.result);
  };

  return committed(tx).then(() => changes);
}

function resolveImports(tx: IDBTransaction, resolutions: Resolution[], records: Pending[], changes: Change[], finish: (records: Pending[]) => void): void {
  const imports = tx.objectStore(IMPORTS);
  const listStore = tx.objectStore(LISTS);
  const itemStore = tx.objectStore(ITEMS);
  const pending = tx.objectStore(PENDING);
  const states = imports.getAll() as IDBRequest<ImportRecord[]>;
  const lists = listStore.getAll() as IDBRequest<List[]>;
  const items = itemStore.getAll() as IDBRequest<Item[]>;
  let remaining = 3;
  const ready = () => {
    if (--remaining) return;
    for (const resolution of resolutions) {
      const outcome = resolution.imported;
      if (!outcome) continue;
      const entry = states.result.find(row => row.id === outcome.id);
      if (!entry) continue;
      if (outcome.rejected === undefined) {
        imports.delete(entry.id);
      } else {
        const origins = new Set(entry.originIds);
        const parent = lists.result.find(row => row.id === entry.list.id);
        const rows = items.result.filter(row => origins.has(row.id));
        // Existing rows moved in are restored, never copied into recovery.
        for (const row of items.result.filter(row => row.listId === entry.list.id && !origins.has(row.id))) {
          const previous = records.find(record => record.id === row.id && record.before && "listId" in record.before && record.before.listId !== entry.list.id)?.before as Item | undefined;
          if (previous) {
            const restored = { ...row, listId: previous.listId, orderKey: previous.orderKey };
            itemStore.put(restored);
            changes.push({ entity: "item", id: row.id, listId: restored.listId, row: restored });
            for (const record of records.filter(record => record.id === row.id)) {
              if (record.listId === entry.list.id) record.listId = previous.listId;
              if (record.before && "listId" in record.before && record.before.listId === entry.list.id) {
                record.before = { ...record.before, listId: previous.listId, orderKey: previous.orderKey };
              }
              if (record.fields.listId === entry.list.id) {
                record.fields = { ...record.fields, listId: previous.listId, afterId: null, beforeId: null };
              }
            }
          }
        }
        for (const row of rows) {
          itemStore.delete(row.id);
          changes.push({ entity: "item", id: row.id, listId: row.listId, row: undefined });
        }
        listStore.delete(entry.list.id);
        changes.push({ entity: "list", id: entry.list.id, row: undefined });
        for (const record of records) {
          if (record.id === entry.list.id || origins.has(record.id)) pending.delete(record.seq);
          else {
            for (const anchor of ["afterId", "beforeId"] as const) {
              if (origins.has(String(record.fields[anchor])) || record.fields[anchor] === entry.list.id) record.fields = { ...record.fields, [anchor]: null };
            }
          }
        }
        records = records.filter(record => record.id !== entry.list.id && !origins.has(record.id));
        if (parent || rows.length) imports.put({ ...entry, list: parent ?? entry.list, status: "rejected", rows,
          message: outcome.rejected } satisfies ImportRecord);
        else imports.delete(entry.id); // the user already deleted the entire local import
      }
      records = records.map(record => {
        const next = { ...record, blockedBy: record.blockedBy?.filter(id => id !== entry.id) };
        pending.put(next);
        return next;
      });
    }
    finish(records);
  };
  states.onsuccess = ready; lists.onsuccess = ready; items.onsuccess = ready;
}

interface Rebase {
  deleted: boolean;
  fields: Fields;
  operations?: Pending[];
}

/** A list whose switch to Order by category is still to be answered (g81): see `absorb`. */
const fillHold = (listId: string) => `fill:${listId}`;

function rebaseIndex(records: Pending[]): Map<string, Rebase> {
  const index = new Map<string, Rebase>();
  // `getAll` on the store returns primary-key order, which is `seq` order,
  // which is the order the changes were made in.
  for (const record of records) {
    if (record.op === "import") continue;
    // A queued change of ordering (g81) has a key of its own, so it is filed
    // here under the list's, where a delta row looks: the setting on this
    // device survives an echo of the list read before the change lands.
    if (isListOrderChange(record)) {
      const key = pendingKey("list", record.id);
      const existing = index.get(key);
      index.set(key, { deleted: Boolean(existing?.deleted), fields: mergeFields(existing?.fields ?? {}, record.fields) });
      if (record.fields.orderBy === "category") index.set(fillHold(record.id), { deleted: false, fields: {} });
      continue;
    }
    if (record.entity === "item" && record.op !== "move") {
      const key = pendingKey("item", record.id);
      const previous = index.get(key);
      index.set(key, { deleted: record.op === "delete" || Boolean(previous?.deleted), fields: {},
        operations: [...previous?.operations ?? [], record] });
      continue;
    }
    if (record.op === "delete") {
      index.set(record.key, { deleted: true, fields: {} });
      continue;
    }
    const existing = index.get(record.key);
    index.set(record.key, {
      deleted: false,
      fields: mergeFields(existing?.fields ?? {}, record.fields),
    });
  }
  return index;
}

/**
 * One incoming row.
 *
 * A tombstone removes rather than stores. Keeping it would mean every read
 * filters forever and the mirror grows without bound, and nothing on a client
 * needs to know a row once existed — "gone" travels in the delta, which is
 * where it belongs.
 *
 * A row that has not visibly changed reports nothing. Rows on the cursor
 * boundary come back on every sync by design (`cursor` is `Date.now() - 1`), so
 * without this every sync would repaint rows a finger may be resting on.
 */
/**
 * Stores each creator a delta names, and reports only a name that changed, so
 * a full download does not repaint every row whose maker is already known.
 * Never rebased: nothing on this device can rename anyone.
 */
/** Every kept history row, and the stamp's own key outside it: `activity:` sorts before any key under it. */
const historyRows = (): IDBKeyRange => IDBKeyRange.bound(HISTORY_PREFIX, HISTORY_PREFIX + String.fromCodePoint(0xffff));

/**
 * What a reply says of the owner's history (g110), in the transaction that
 * stores the reply. A reply with no stamp means history cannot be read now:
 * the stamp goes, and every kept page with it. A moved `epoch` means a name or
 * label kept on a page may have changed or been retired, so every kept page
 * goes too, before anything can show it. A moved `head` is only stored: a
 * kept series knows the head it was read at, and is brought up to date when
 * it is next shown.
 */
function absorbStamp(meta: IDBObjectStore, stored: unknown, incoming: unknown, changes: Change[]): void {
  const stamp = readsStamp(incoming) ? incoming : null;
  const held = readsStamp(stored) ? stored : null;
  if (held === null && stamp === null) return;
  if (held !== null && stamp !== null && held.head === stamp.head && held.epoch === stamp.epoch) return;
  if (stamp === null || (held !== null && held.epoch !== stamp.epoch)) meta.delete(historyRows());
  if (stamp === null) meta.delete(STAMP_KEY);
  else meta.put({ key: STAMP_KEY, value: { v: stamp.v, head: stamp.head, epoch: stamp.epoch } });
  changes.push({ entity: "activity", stamp });
}

/** A kept series and the stamp it may be shown under (g110). */
export async function readHistory(db: IDBDatabase, target: HistoryTarget): Promise<{ stamp: ActivityStamp | null; series: SeriesRow | null }> {
  const meta = db.transaction(META, "readonly").objectStore(META);
  const [stamp, series] = await Promise.all([
    done(meta.get(STAMP_KEY) as IDBRequest<{ value: unknown } | undefined>),
    done(meta.get(seriesKey(target)) as IDBRequest<{ value: SeriesRow } | undefined>),
  ]);
  return { stamp: readsStamp(stamp?.value) ? (stamp!.value as ActivityStamp) : null, series: series?.value ?? null };
}

export async function readDetail(db: IDBDatabase, actionId: string): Promise<DetailRow | null> {
  const meta = db.transaction(META, "readonly").objectStore(META);
  return (await done(meta.get(detailKey(actionId)) as IDBRequest<{ value: DetailRow } | undefined>))?.value ?? null;
}

/**
 * Keeps a series or a detail read under `epoch` (g110), and drops whatever
 * the bounds no longer allow. Kept only while the stamp still has that
 * epoch: a page read before names changed is never written after the
 * change dropped the rest. Answers whether it was kept.
 */
export function keepHistory(db: IDBDatabase, epoch: string, row: { series: SeriesRow } | { detail: DetailRow }): Promise<boolean> {
  const tx = db.transaction(META, "readwrite");
  const meta = tx.objectStore(META);
  let kept = false;
  const stamp = meta.get(STAMP_KEY) as IDBRequest<{ value: unknown } | undefined>;
  stamp.onsuccess = () => {
    const held = stamp.result?.value;
    if (!readsStamp(held) || held.epoch !== epoch) return;
    kept = true;
    if ("series" in row) meta.put({ key: seriesKey(row.series.target), value: row.series });
    else meta.put({ key: detailKey(row.detail.actionId), value: row.detail });
    const all = meta.getAll(historyRows()) as IDBRequest<{ key: string; value: unknown }[]>;
    all.onsuccess = () => {
      const series = all.result.filter((entry) => entry.key.startsWith(SERIES_PREFIX)).map((entry) => entry.value as SeriesRow);
      const details = all.result.filter((entry) => entry.key.startsWith(DETAIL_PREFIX)).map((entry) => entry.value as DetailRow);
      for (const key of evictions(series, details)) meta.delete(key);
    };
  };
  return committed(tx).then(() => kept);
}

/** Every kept history row goes, and the stamp stays (g110): a change of account on this device. */
export function forgetHistory(db: IDBDatabase): Promise<void> {
  const tx = db.transaction(META, "readwrite");
  tx.objectStore(META).delete(historyRows());
  return committed(tx);
}

function absorbCreators(meta: IDBObjectStore, incoming: Creator[], changes: Change[]): void {
  for (const creator of incoming) {
    const row: Creator = creator.deleted ? { id: creator.id, name: null, deleted: true } : { id: creator.id, name: creator.name };
    const key = CREATOR_KEY + row.id;
    const request = meta.get(key) as IDBRequest<{ key: string; value: Creator } | undefined>;
    request.onsuccess = () => {
      const stored = request.result?.value;
      if (stored && stored.name === row.name && stored.deleted === row.deleted) return;
      meta.put({ key, value: row });
      changes.push({ entity: "creator", id: row.id, row });
    };
  }
}

function absorb<T extends { id: string; deletedAt: number | null }>(
  store: IDBObjectStore,
  rebase: Map<string, Rebase>,
  entity: "list" | "item",
  fields: readonly string[],
  /** See `ITEM_VISIBLE`: what a pending change may rebase is not what a screen shows. */
  visible: readonly string[],
  incoming: T,
  report: (id: string, row: T | undefined) => void,
): void {
  const pending = rebase.get(pendingKey(entity, incoming.id));
  /**
   * A queued move lives under its own key, so the lookup above never sees it —
   * and `orderKey` is not a rebasable field, so without this the server's key
   * overwrites the local one and the row visibly snaps back to where it was
   * until the move is sent and its answer comes round.
   *
   * The move itself is not applied here; only the position already on screen is
   * held. The server decides the real key when the move lands.
   */
  const moving = rebase.get(pendingKey(entity, incoming.id, "move"));
  const request = store.get(incoming.id) as IDBRequest<T | undefined>;

  request.onsuccess = () => {
    const stored = request.result;

    if (incoming.deletedAt !== null || pending?.deleted) {
      if (stored) {
        store.delete(incoming.id);
        report(incoming.id, undefined);
      }
      return;
    }

    const rebased = pending?.operations
      ? replayItem(incoming as unknown as Item, pending.operations) as unknown as T
      : pending ? ({ ...incoming, ...pick(pending.fields, fields) } as T) : incoming;
    // Import acceptance can precede a queued list deletion by an entire write
    // window. Its echoed items must stay deleted locally throughout that wait.
    if (entity === "item" && "listId" in rebased && rebase.get(pendingKey("list", String(rebased.listId)))?.deleted) {
      if (stored) { store.delete(incoming.id); report(incoming.id, undefined); }
      return;
    }
    let held =
      moving && !moving.deleted && stored && "orderKey" in stored
        ? ({ ...rebased, orderKey: (stored as { orderKey: unknown }).orderKey } as T)
        : rebased;

    // While a switch to Order by category waits for its answer, a category
    // this device filled stays: a delta read before the switch lands still
    // says none, and would empty it under the person's eye. Only a blank is
    // held off; a category the server does name wins as usual. The switch's
    // own reply carries the whole list and settles every row.
    const filling = entity === "item" && stored !== undefined
      && rebase.has(fillHold(String((held as { listId?: unknown }).listId)))
      && ((held as { category?: unknown }).category ?? null) === null
      && Boolean((stored as { category?: unknown }).category);
    if (filling) held = { ...held, category: (stored as { category?: unknown }).category } as T;

    store.put(held);
    /**
     * Compared on what a screen shows — which now includes `orderKey`, because
     * a row that moved has visibly moved.
     *
     * Two reasons this does not reintroduce the repainting the comparison
     * exists to prevent. Rows on the cursor boundary come back on every sync by
     * design, and they carry the key they already had, so they still report
     * nothing. And a row with a *queued* move has had the local key held above,
     * so `stored` and `held` agree and it stays quiet until the server answers.
     *
     * What changes is the case that was silently broken: a reorder made on
     * another device arrives with a new key and no other difference, and used
     * to be written here and told to nobody — leaving every screen drawing the
     * old order and the store's in-memory copy stale against its own database
     * until a reload.
     */
    if (!stored || !same(stored, held, visible)) report(incoming.id, held);
  };
}


/** Replay intent over authoritative rows. A stale occurrence never consumes a newer one. */
function replayItem(incoming: Item, records: Pending[]): Item {
  let row = incoming;
  for (const record of records) {
    if (record.op === "delete") continue;
    if (record.op === "upsert" && record.operationId) continue; // the server already holds this creation
    if (record.ordered && record.expectedRevision !== undefined &&
        record.expectedRevision !== (row.occurrenceRevision ?? 0)) continue;
    try {
      if (record.op === "complete" || record.op === "undo") {
        row = occurrence(row, record.op, { operationId: record.operationId!,
          expectedRevision: record.expectedRevision!, ...record.fields });
      } else {
        row = editOccurrence(row, pick(record.fields, ITEM_FIELDS));
      }
    } catch {
      // The server will reject this command too. Keep its authoritative state
      // until the verdict drains the record and explains the conflict.
    }
  }
  return row;
}

function pick(fields: Fields, allowed: readonly string[]): Fields {
  const picked: Fields = {};
  for (const key of allowed) {
    if (fields[key] !== undefined) picked[key] = fields[key];
  }
  return picked;
}

/** Timestamps are deliberately not compared — only what a screen can show. */
function same(a: Record<string, unknown>, b: Record<string, unknown>, fields: readonly string[]) {
  return fields.every((key) => equal(a[key], b[key]));
}

/**
 * Structural, and key-order blind. `due` is a nested object that reaches here
 * from two directions — the server's serialiser and this device's compose form
 * — and comparing them as JSON text would call them different over nothing but
 * the order the keys were written in.
 */
function equal(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  const left = Object.keys(a).sort();
  const right = Object.keys(b).sort();
  if (left.length !== right.length) return false;
  return left.every(
    (key, index) =>
      key === right[index] &&
      equal((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
}
